import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';

const SUPPORT_EMAIL = 'support@notifications.angle.dating';
const APP_NAME = 'Angle!';
const COMPANY_NAME = 'Angle App';
const EFFECTIVE_DATE = '04 April 2026';
const APP_URL = 'https://angle.dating/';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">{title}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Privacy Policy | Angle! Photo Ranking App</title>
        <meta name="description" content="Read the Privacy Policy for Angle! – learn how we collect, use, and protect your personal data when you use our AI photo ranking service." />
        <link rel="canonical" href="https://angle.dating/privacy-policy" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://angle.dating/privacy-policy" />
        <meta property="og:title" content="Privacy Policy | Angle!" />
        <meta property="og:description" content="Read the Privacy Policy for Angle! – learn how we collect, use, and protect your personal data." />
        <meta property="og:site_name" content="Angle!" />
      </Helmet>
      <Header
        userProp={null}
        onNavigate={() => {}}
        onPurchaseCredits={() => {}}
        onLogout={() => {}}
      />
      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title block */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
          <p className="text-gray-500 text-sm">
            Effective Date: <strong>{EFFECTIVE_DATE}</strong> &nbsp;·&nbsp; Last Updated: <strong>{EFFECTIVE_DATE}</strong>
          </p>
          <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg text-sm text-cyan-800">
            Your privacy matters to us. This policy explains what data we collect, how we use it, and your rights regarding your personal information.
          </div>
        </div>

        <Section title="1. Who We Are">
          <p>
            {COMPANY_NAME} ("we," "us," or "our") operates the {APP_NAME} application available at <a href={APP_URL} className="text-violet-600 underline">{APP_URL}</a>. We are the data controller for personal information collected through the Service.
          </p>
          <p>
            For privacy-related inquiries, contact our team at: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-600 underline">{SUPPORT_EMAIL}</a>
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>2.1 Information You Provide Directly</strong></p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account Information:</strong> Name, email address, and password when you register.</li>
            <li><strong>Payment Information:</strong> Billing details (card number, expiry, CVV) are collected and processed directly by Stripe, Inc. We receive only a payment confirmation token and the last four digits of your card — we never store your full card details.</li>
            <li><strong>Photographs:</strong> Images you upload for analysis. These are processed to generate rankings and scores and are stored temporarily to display your results.</li>
            <li><strong>Communications:</strong> Messages you send to our support team.</li>
          </ul>

          <p className="mt-4"><strong>2.2 Information Collected Automatically</strong></p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Usage Data:</strong> Pages visited, features used, session duration, and interaction patterns within the app.</li>
            <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, and IP address.</li>
            <li><strong>Log Data:</strong> Server logs including timestamps, error reports, and API request details.</li>
            <li><strong>Cookies and Similar Technologies:</strong> We use session cookies for authentication and functional cookies to remember your preferences. See Section 8 for details.</li>
          </ul>

          <p className="mt-4"><strong>2.3 Information from Third Parties</strong></p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Authentication Providers:</strong> If you sign in using a third-party provider (e.g., Google), we receive your name and email address from that provider.</li>
            <li><strong>Payment Processors:</strong> Stripe provides us with transaction status, payment method type, and billing country.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use the information we collect for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Service Delivery:</strong> To process your photo uploads, generate AI rankings, manage your credit balance, and display your analysis history.</li>
            <li><strong>Account Management:</strong> To create and maintain your account, authenticate your identity, and communicate with you about your account.</li>
            <li><strong>Payment Processing:</strong> To process credit purchases and send transaction receipts via email.</li>
            <li><strong>Customer Support:</strong> To respond to your inquiries, resolve disputes, and troubleshoot issues.</li>
            <li><strong>Service Improvement:</strong> To analyze usage patterns, fix bugs, and improve the performance and features of the Service. We do not use your uploaded photographs to train our AI models without your explicit consent.</li>
            <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes, including fraud prevention and anti-money-laundering obligations.</li>
            <li><strong>Communications:</strong> To send transactional emails (receipts, account confirmations, password resets) and, with your consent, promotional communications about new features or offers.</li>
          </ul>
        </Section>

        <Section title="4. Legal Basis for Processing (GDPR)">
          <p>If you are located in the European Economic Area (EEA) or United Kingdom, we process your personal data under the following legal bases:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Contract Performance:</strong> Processing necessary to provide the Service you have requested (account creation, photo analysis, payment processing).</li>
            <li><strong>Legitimate Interests:</strong> Improving the Service, preventing fraud, and ensuring security — where these interests are not overridden by your rights.</li>
            <li><strong>Legal Obligation:</strong> Compliance with applicable laws and regulations.</li>
            <li><strong>Consent:</strong> For marketing communications and any optional data processing activities. You may withdraw consent at any time.</li>
          </ul>
        </Section>

        <Section title="5. How We Share Your Information">
          <p>We do not sell your personal information. We share your data only in the following circumstances:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Service Providers:</strong> We share data with trusted third-party vendors who help us operate the Service:
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Supabase</strong> — database hosting and authentication</li>
                <li><strong>Stripe</strong> — payment processing</li>
                <li><strong>Resend</strong> — transactional email delivery</li>
                <li><strong>AWS (Amazon Web Services)</strong> — AI inference infrastructure</li>
                <li><strong>Google (Gemini API)</strong> — AI-powered profile insights</li>
              </ul>
              Each provider is bound by data processing agreements and may only use your data to provide services to us.
            </li>
            <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, court order, or governmental authority, or to protect the rights, property, or safety of {COMPANY_NAME}, our users, or the public.</li>
            <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you before your data is transferred and becomes subject to a different privacy policy.</li>
          </ul>
        </Section>

        <Section title="6. Data Retention">
          <p>
            <strong>Account Data:</strong> We retain your account information for as long as your account is active. If you delete your account, we will delete or anonymize your personal data within 30 days, except where retention is required by law.
          </p>
          <p>
            <strong>Photographs:</strong> Uploaded photos are retained for the duration of your account to display analysis history. You may delete individual analyses from your dashboard at any time.
          </p>
          <p>
            <strong>Transaction Records:</strong> Payment and billing records are retained for a minimum of 7 years to comply with financial and tax regulations.
          </p>
          <p>
            <strong>Log Data:</strong> Server logs are retained for up to 90 days for security and debugging purposes.
          </p>
        </Section>

        <Section title="7. Your Rights">
          <p>Depending on your location, you may have the following rights regarding your personal data:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data ("right to be forgotten"), subject to legal retention requirements.</li>
            <li><strong>Portability:</strong> Request your data in a structured, machine-readable format.</li>
            <li><strong>Objection:</strong> Object to processing based on legitimate interests or for direct marketing purposes.</li>
            <li><strong>Restriction:</strong> Request that we restrict processing of your data in certain circumstances.</li>
            <li><strong>Withdraw Consent:</strong> Where processing is based on consent, withdraw it at any time without affecting the lawfulness of prior processing.</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-600 underline">{SUPPORT_EMAIL}</a>. We will respond within 30 days. You also have the right to lodge a complaint with your local data protection authority.
          </p>
          <p>
            <strong>California Residents (CCPA):</strong> California residents have additional rights including the right to know what personal information is collected, the right to delete personal information, and the right to opt out of the sale of personal information. We do not sell personal information. To submit a CCPA request, contact us at the email above.
          </p>
        </Section>

        <Section title="8. Cookies and Tracking Technologies">
          <p>We use the following types of cookies:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Strictly Necessary Cookies:</strong> Required for authentication and core functionality. These cannot be disabled.</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences (e.g., language, display settings).</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Service (e.g., Google Analytics). These are only set with your consent.</li>
          </ul>
          <p>
            You can control cookies through your browser settings. Disabling certain cookies may affect the functionality of the Service.
          </p>
        </Section>

        <Section title="9. Data Security">
          <p>
            We implement industry-standard security measures to protect your personal information, including:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>TLS/HTTPS encryption for all data in transit.</li>
            <li>Encrypted storage for sensitive data at rest.</li>
            <li>Row-level security policies on our database to ensure users can only access their own data.</li>
            <li>Regular security reviews and access controls.</li>
          </ul>
          <p>
            However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your data, we cannot guarantee absolute security. In the event of a data breach that affects your rights and freedoms, we will notify you and relevant authorities as required by law.
          </p>
        </Section>

        <Section title="10. International Data Transfers">
          <p>
            Your information may be transferred to and processed in countries other than your country of residence, including the United States, where our service providers operate. When we transfer data from the EEA or UK, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the European Commission.
          </p>
        </Section>

        <Section title="11. Children's Privacy">
          <p>
            The Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that we have collected data from a person under 18 without parental consent, we will take steps to delete that information promptly. If you believe we have inadvertently collected data from a minor, please contact us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-600 underline">{SUPPORT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="12. Third-Party Links">
          <p>
            The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of those third parties. We encourage you to review the privacy policies of any third-party sites you visit.
          </p>
        </Section>

        <Section title="13. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last Updated" date at the top of this page and notify you by email or in-app notification where required by law. Your continued use of the Service after the effective date of the revised policy constitutes your acceptance of the changes.
          </p>
        </Section>

        <Section title="14. Contact Us">
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="mt-3 p-4 bg-gray-100 rounded-lg">
            <p className="font-semibold text-gray-900">{COMPANY_NAME} — Privacy Team</p>
            <p className="text-gray-700">
              Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-600 underline">{SUPPORT_EMAIL}</a>
            </p>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            We aim to respond to all privacy-related inquiries within 30 days.
          </p>
        </Section>
      </main>
      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">© {new Date()?.getFullYear()} {COMPANY_NAME}. All rights reserved.</p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/terms-of-service" className="hover:text-gray-200 transition-colors">Terms of Service</Link>
            <Link to="/privacy-policy" className="hover:text-gray-200 transition-colors font-medium text-gray-300">Privacy Policy</Link>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-gray-200 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
